import { useEffect, useState } from 'react'
import Modal from '../../../../common/components/molecule/Modal/Modal'
import ButtonV2 from '../../../../common/components/atom/ButtonV2/Button'
import { useToast } from '../../../../common/providers/ToastProvider/useToast'
import momiAPI from '../../../../features/momi/service/momiAPI'
import { useQueryClient } from 'react-query'
import { parseCsv } from '../../../../common/utils/csv'

interface ImportMomiCsvDialogProps {
  open: boolean
  onClose: () => void
}

const REQUIRED_HEADERS = ['momi_uuid']
const ACCEPTED_HEADERS = ['momi_uuid', 'momi_name']

interface RowResult {
  line: number
  uuid: string
  status: 'ok' | 'error'
  message?: string
}

export default function ImportMomiCsvDialog({
  open,
  onClose,
}: ImportMomiCsvDialogProps) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [file, setFile] = useState<File | null>(null)
  const [rows, setRows] = useState<Record<string, string>[]>([])
  const [parseErrors, setParseErrors] = useState<string[]>([])
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [results, setResults] = useState<RowResult[]>([])

  useEffect(() => {
    if (open) {
      setFile(null)
      setRows([])
      setParseErrors([])
      setImporting(false)
      setProgress({ done: 0, total: 0 })
      setResults([])
    }
  }, [open])

  const handleFile = async (f: File) => {
    setFile(f)
    setResults([])
    const text = await f.text()
    const { rows: parsed, errors } = parseCsv(text, REQUIRED_HEADERS)
    const errMsgs = errors.map((e) => e.message)
    const filtered = parsed.filter((r) => (r.momi_uuid ?? '').trim() !== '')
    setRows(filtered)
    setParseErrors(errMsgs)
  }

  const handleImport = async () => {
    if (rows.length === 0 || importing) return
    setImporting(true)
    setResults([])
    setProgress({ done: 0, total: rows.length })

    const out: RowResult[] = []
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      const uuid = (row.momi_uuid ?? '').trim()
      const name =
        (row.momi_name ?? '').trim() !== ''
          ? (row.momi_name ?? '').trim()
          : 'MoMi-' + uuid.slice(0, 8)

      try {
        await momiAPI.createMomi({ momi_uuid: uuid, momi_name: name })
        out.push({ line: i + 2, uuid, status: 'ok' })
      } catch (err: any) {
        const backendMsg = err?.response?.data?.message
        const status = err?.response?.status
        const msg =
          backendMsg ??
          (status === 409 || status === 400
            ? 'UUID déjà enregistré ou invalide'
            : err?.message ?? 'Erreur inconnue')
        out.push({ line: i + 2, uuid, status: 'error', message: msg })
      }
      setProgress({ done: i + 1, total: rows.length })
    }

    setResults(out)
    setImporting(false)
    queryClient.invalidateQueries('momis')

    const okCount = out.filter((r) => r.status === 'ok').length
    const errCount = out.length - okCount
    if (errCount === 0) {
      toast.success(`${okCount} MoMi(s) importé(s)`)
    } else if (okCount === 0) {
      toast.error(`Échec de l'import (${errCount} erreur(s))`)
    } else {
      toast.success(`${okCount} importé(s), ${errCount} en erreur`)
    }
  }

  const hasBlockingError = parseErrors.length > 0

  return (
    <Modal isOpen={open} handleClose={importing ? undefined : onClose}>
      <h2>Importer des MoMis depuis un CSV</h2>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          marginTop: '1rem',
          minWidth: 420,
        }}
      >
        <p style={{ margin: 0, fontSize: 14, color: '#555' }}>
          Colonnes attendues&nbsp;: <code>{ACCEPTED_HEADERS.join(', ')}</code>.
          <br />
          <code>momi_uuid</code> est obligatoire, <code>momi_name</code> est optionnel.
          <br />
          <em>
            Chaque MoMi est unique dans MinigridUI&nbsp;: un UUID déjà enregistré
            (sur ce compte ou un autre) sera rejeté.
          </em>
        </p>

        <input
          type="file"
          accept=".csv,text/csv"
          disabled={importing}
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) handleFile(f)
          }}
        />

        {file && (
          <div style={{ fontSize: 13, color: '#333' }}>
            Fichier&nbsp;: <strong>{file.name}</strong> — {rows.length} ligne(s)
            détectée(s)
          </div>
        )}

        {parseErrors.length > 0 && (
          <div style={{ color: '#b00020', fontSize: 13 }}>
            {parseErrors.map((e, idx) => (
              <div key={idx}>• {e}</div>
            ))}
          </div>
        )}

        {importing && (
          <div style={{ fontSize: 13 }}>
            Import en cours… {progress.done}/{progress.total}
          </div>
        )}

        {results.length > 0 && (
          <div
            style={{
              maxHeight: 200,
              overflowY: 'auto',
              border: '1px solid #eee',
              borderRadius: 4,
              padding: '0.5rem',
              fontSize: 12,
            }}
          >
            {results.map((r) => (
              <div
                key={r.line}
                style={{ color: r.status === 'ok' ? '#1b7a1b' : '#b00020' }}
              >
                L{r.line} — {r.uuid.slice(0, 12)}…{' '}
                {r.status === 'ok' ? 'OK' : `✗ ${r.message}`}
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end',
          marginTop: '1.5rem',
        }}
      >
        <ButtonV2
          onClick={onClose}
          variant="outlined"
          color="secondary"
          size="medium"
          disabled={importing}
        >
          Fermer
        </ButtonV2>
        <ButtonV2
          onClick={handleImport}
          variant="contained"
          color="primary"
          size="medium"
          disabled={importing || rows.length === 0 || hasBlockingError}
        >
          {importing ? 'Import…' : `Importer (${rows.length})`}
        </ButtonV2>
      </div>
    </Modal>
  )
}
